import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SearchForm from "../../components/SearchForm";
import { getSimpleColor, getBgColor, URL, MEDIA_URL, getToken } from "../../utils";
import Dropzone from "../../components/Dropzone";
import ChartColumn from "../../components/ChartColumn";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Fonction utilitaire pour gérer les erreurs
const handleApiError = (error, setError) => {
  if (error.response) {
    setError(`Erreur ${error.response.status}: ${error.response.statusText}`);
  } else if (error.request) {
    setError("Erreur de requête. Veuillez vérifier votre connexion.");
  } else {
    setError(`Erreur: ${error.message}`);
  }
};

export default function SellPointDetails() {
  const [magasin, setMagasin] = useState({});
  const [nom, setNom] = useState("");
  const [photo, setPhoto] = useState(null); // Changé de "" à null pour stocker le fichier
  const [modalType, setModalType] = useState(null); // 'add', 'edit', 'deactivate'
  const [validationErrors, setValidationErrors] = useState({});
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [articlesStatistics, setArticlesStatistics] = useState({
    carton: { labels: [], values: [] },
    unite: { labels: [], values: [] },
  });
  const token = getToken();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    chiffre_affaire_jour: 0,
    chiffre_affaire_mois: 0,
    quota_journalier: 0.0,
    quota_mensuel: 0.0,
  });
  const [categoriesNumber, setCategoriesNumber] = useState(0);

  useEffect(() => {
    fetchAllData();
  }, [categoriesNumber, id]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [magasinResponse, articlesResponse, categoriesResponse] =
        await Promise.all([
          axios.get(`${URL}Magasins/magasin-detail/${id}`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
          axios.post(
            `${URL}Magasins/quantite-by-stock-item`,
            { magasin_id: id },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          ),
          axios.get(`${URL}Magasins/categories-list/${id}`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);

      const cartonData = { labels: [], values: [] };
      const uniteData = { labels: [], values: [] };
      articlesResponse.data.unite.forEach((item) => {
        uniteData.labels.push(item.nom);
        uniteData.values.push(item.quantite);
      });

      articlesResponse.data.carton.forEach((item) => {
        cartonData.labels.push(item.nom);
        cartonData.values.push(item.quantite);
      });

      setMagasin(magasinResponse.data);

      if (!magasinResponse.data?.is_entrepot) {
        fetchStats();
      }

      setArticlesStatistics({ carton: cartonData, unite: uniteData });
      setData(categoriesResponse.data);
      setCategoriesNumber(categoriesResponse.data.length);
    } catch (err) {
      handleApiError(err, setError);
      toast.error("Erreur lors de la récupération des données !");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const statsResponse = await axios.get(
        `${URL}Magasins/magasin-statistics/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setStats(statsResponse.data);
    } catch (err) {
      handleApiError(err, setError);
      toast.error("Erreur lors de la récupération des statistiques !");
    }
  };

  const addCategory = async () => {
    setValidationErrors({});
    try {
      const formData = new FormData();
      formData.append("nom", nom);
      formData.append("nom_magasin", id);
      if (photo) {
        formData.append("photo", photo);
      }

      const response = await axios.post(
        `${URL}Magasins/create-category`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 201) {
        setModalType(null);
        setCategoriesNumber(categoriesNumber + 1);
        setNom("");
        setPhoto(null);
        toast.success("Catégorie ajoutée avec succès !");
      }
    } catch (err) {
      toast.error("Erreur lors de la création de la catégorie !");
      if (err.response?.status === 400) {
        setValidationErrors(err.response.data);
      } else {
        handleApiError(err, setError);
      }
    }
  };

  const editCategory = async () => {
    setValidationErrors({});
    try {
      const formData = new FormData();
      formData.append("nom", nom);
      formData.append("nom_magasin", id);
      if (photo && photo instanceof File) {
        formData.append("photo", photo);
      }

      const response = await axios.put(
        `${URL}Magasins/edit-category/${currentCategory.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setModalType(null);
        setNom("");
        setPhoto(null);
        setCategoriesNumber(categoriesNumber + 1); // Déclenche un rafraîchissement
        toast.success("Catégorie modifiée avec succès !");
      }
    } catch (err) {
      toast.error("Erreur lors de la modification de la catégorie !");
      if (err.response?.status === 400) {
        setValidationErrors(err.response.data);
      } else {
        handleApiError(err, setError);
      }
    }
  };

  const deactivateCategory = async () => {
    try {
      const response = await axios.patch(
        `${URL}Magasins/deactivate-category/${currentCategory.id}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 200) {
        setModalType(null);
        setCategoriesNumber(categoriesNumber + 1); // Déclenche un rafraîchissement
        toast.success("Catégorie désactivée avec succès !");
      }
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error(err.response.data.error);
      } else {
        handleApiError(err, setError);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (modalType === "add") addCategory();
    else if (modalType === "edit") editCategory();
  };

  const handleDeactivate = () => {
    deactivateCategory();
  };

  const openModal = (type, category = null) => {
    // Réinitialiser l'état avant d'ouvrir un nouveau modal
    if (modalType !== type) {
      setCurrentCategory({});
      setNom("");
      setPhoto(null);
    }

    // Définir le type de modal
    setModalType(type);

    // Si une catégorie est fournie, définir les données de la catégorie
    if (category) {
      setCurrentCategory(category);

      // Ne définir nom et photo que pour le mode "edit"
      if (type === "edit") {
        setNom(category.nom);
        setPhoto(category.photo); // Peut être une URL ou null
      }
    }
  };

  const closeModal = () => {
    setModalType(null);
    setValidationErrors({});
    setNom("");
    setPhoto(null);
  };

  const handleFileUpload = (file) => {
    setPhoto(file);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center">
          <div className="loader"></div>
          <div className="w-10 h-10 border-2 border-t-2 border-gray-200 rounded-full animate-spin my-4"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer />
      <div className="shadow-md bg-white p-4 sticky top-16 z-40 space-y-2">
        <h1 className="text-xl font-bold flex items-center gap-2">
          <svg
            className="w-6 h-6"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              fillRule="evenodd"
              d="M3 6a3 3 0 1 1 4 2.83v6.34a3.001 3.001 0 1 1-2 0V8.83A3.001 3.001 0 0 1 3 6Zm11.207-2.707a1 1 0 0 1 0 1.414L13.914 5H15a4 4 0 0 1 4 4v6.17a3.001 3.001 0 1 1-2 0V9a2 2 0 0 0-2-2h-1.086l.293.293a1 1 0 0 1-1.414 1.414l-2-2a1 1 0 0 1 0-1.414l2-2a1 1 0 0 1 1.414 0Z"
              clipRule="evenodd"
            />
          </svg>
          {magasin.nom}
        </h1>
        <div className="flex justify-between items-center">
          <SearchForm placeholder="Rechercher un point de vente" />
          {/* Large screens buttons */}
          <div className="flex items-center justify-end gap-4 max-sm:hidden">
            <button
              onClick={() => openModal("add")}
              className="p-2 px-4 rounded-md bg-amber-500 text-white hover:bg-amber-600 focus:ring-2 border-2 border-amber-500 focus:ring-offset-2 focus:ring-amber-500 text-sm"
            >
              Ajouter une catégorie
            </button>
          </div>
          {/* Resposive buttons */}
          <div className="flex items-center justify-end gap-4 sm:hidden">
            <button
              onClick={() => openModal("add")}
              className="p-2 rounded-md bg-amber-500 text-white hover:bg-amber-600 focus:ring-2 border-2 border-amber-500 focus:ring-offset-2 focus:ring-amber-500 text-sm"
            >
              <svg
                className="w-6 h-6"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  fillRule="evenodd"
                  d="M4.857 3A1.857 1.857 0 0 0 3 4.857v4.286C3 10.169 3.831 11 4.857 11h4.286A1.857 1.857 0 0 0 11 9.143V4.857A1.857 1.857 0 0 0 9.143 3H4.857Zm10 0A1.857 1.857 0 0 0 13 4.857v4.286c0 1.026.831 1.857 1.857 1.857h4.286A1.857 1.857 0 0 0 21 9.143V4.857A1.857 1.857 0 0 0 19.143 3h-4.286Zm-10 10A1.857 1.857 0 0 0 3 14.857v4.286C3 20.169 3.831 21 4.857 21h4.286A1.857 1.857 0 0 0 11 19.143v-4.286A1.857 1.857 0 0 0 9.143 13H4.857ZM18 14a1 1 0 1 0-2 0v2h-2a1 1 0 1 0 0 2h2v2a1 1 0 1 0 2 0v-2h2a1 1 0 1 0 0-2h-2v-2Z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Rapport du jour */}
      {!magasin.is_entrepot && (
        <div className="m-4 rounded-md p-1 space-y-5">
          <h2 className="mb-4 text-xl">Tableau de bord</h2>
          <div className="grid grid-flow-col gap-4 overflow-x-auto bg-white shadow-md p-2">
            {/* Carte Chiffre d'affaire du jour */}
            <div className="h-44 min-w-52 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 space-y-3 shadow-lg border border-gray-700 hover:border-amber-500 transition-all">
              <div className="flex justify-between items-start">
                <span className="text-amber-400 p-2 flex items-center justify-center rounded-lg bg-amber-500 bg-opacity-10">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </span>
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-900 bg-opacity-50 text-green-400">
                  Quotidien
                </span>
              </div>
              <h3 className="text-3xl font-bold text-white">
                {stats.chiffre_affaire_jour.toLocaleString()}fr
              </h3>
              <div>
                <p className="text-gray-300">Chiffre d'affaire jour</p>
                <p className="text-xs text-gray-400 mt-1">
                  Objectif: {stats.quota_journalier.toLocaleString()}fr
                </p>
              </div>
            </div>

            {/* Carte Chiffre d'affaire du mois */}
            <div className="h-44 min-w-52 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 space-y-3 shadow-lg border border-gray-700 hover:border-blue-400 transition-all">
              <div className="flex justify-between items-start">
                <span className="text-blue-400 p-2 flex items-center justify-center rounded-lg bg-blue-500 bg-opacity-10">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V6a1 1 0 011-1h11a1 1 0 011 1v7.1M8 7a3 3 0 100 6 3 3 0 000-6zm0 0h5m4 0h2m-6 6h2m4 0h1m-4 4h5m-9-4a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </span>
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-900 bg-opacity-50 text-blue-400">
                  Mensuel
                </span>
              </div>
              <h3 className="text-3xl font-bold text-white">
                {stats.chiffre_affaire_mois.toLocaleString()}fr
              </h3>
              <div>
                <p className="text-gray-300">Chiffre d'affaire mois</p>
                <p className="text-xs text-gray-400 mt-1">
                  Objectif: {stats.quota_mensuel.toLocaleString()}fr
                </p>
              </div>
            </div>

            {/* Carte Quota journalier */}
            <div className="h-44 min-w-52 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 space-y-3 shadow-lg border border-gray-700 hover:border-purple-400 transition-all">
              <div className="flex justify-between items-start">
                <span className="text-purple-400 p-2 flex items-center justify-center rounded-lg bg-purple-500 bg-opacity-10">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </span>
                <span
                  className="badge"
                  data-badge={stats.quota_journalier + "% atteint"}
                  style={{
                    "--color": getSimpleColor(stats.quota_journalier / 100),
                    "--bg": getBgColor(stats.quota_journalier / 100),
                    "--bg-opacity": 0.2,
                  }}
                >
                  {stats.quota_journalier}%
                </span>
              </div>
              <h3 className="text-3xl font-bold text-white">
                {magasin.objectif_journalier}fr
              </h3>
              <div>
                <p className="text-gray-300">Quota journalier</p>
                <p className="text-xs text-gray-400 mt-1">
                  {stats.quota_journalier >= 100 ? "Atteint ✓" : "En cours..."}
                </p>
              </div>
            </div>

            {/* Carte Quota mensuel */}
            <div className="h-44 min-w-52 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 space-y-3 shadow-lg border border-gray-700 hover:border-emerald-400 transition-all">
              <div className="flex justify-between items-start">
                <span className="text-emerald-400 p-2 flex items-center justify-center rounded-lg bg-emerald-500 bg-opacity-10">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </span>
                <span
                  className="badge"
                  data-badge={stats.quota_mensuel + "% atteint"}
                  style={{
                    "--color": getSimpleColor(stats.quota_mensuel / 100),
                    "--bg": getBgColor(stats.quota_mensuel / 100),
                    "--bg-opacity": 0.2,
                  }}
                >
                  {stats.quota_mensuel}%
                </span>
              </div>
              <h3 className="text-3xl font-bold text-white">
                {magasin.objectif_mensuel}fr
              </h3>
              <div>
                <p className="text-gray-300">Quota mensuel</p>
                <p className="text-xs text-gray-400 mt-1">
                  {stats.quota_mensuel >= 100 ? "Atteint ✓" : "En cours..."}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des catégories */}
      <div className="m-4 rounded-md p-1 space-y-5">
        <h2 className="mb-4 text-xl">Liste des catégories</h2>
        {data.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 min-h-96">
            {data.map((item) => (
              <div
                key={item.id}
                className="shadow-md pb-4 h-max rounded-md bg-white"
              >
                <img
                  src={
                    item.photo
                      ? `${MEDIA_URL}${item.photo}`
                      : "https://i.pinimg.com/736x/78/16/50/781650001b4c216959d8d5bcac8bb2e5.jpg"
                  }
                  alt={item.nom}
                  className="h-32 w-full object-cover rounded-t-lg mb-1"
                />
                <div className="space-y-3 p-4">
                  <div className="flex justify-between items-center space-x-2 relative">
                    <h2 className="text-xl font-medium">{item.nom}</h2>
                    <div className="flex gap-2 items-center justify-end">
                      <button
                        onClick={() => openModal("deactivate", item)} // Corrigé pour passer la catégorie
                        className="bg-red-500 hover:bg-red-700 text-white p-2 rounded-md"
                      >
                        <svg
                          className="w-4 h-4"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => openModal("edit", item)}
                        className="bg-amber-500 hover:bg-amber-700 text-white p-2 rounded-md"
                      >
                        <svg
                          className="w-4 h-4"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z"
                          />
                        </svg>
                      </button>
                      <Link
                        to={`/stock/sell-point-details/${id}/category/${item.id}`}
                        className="bg-green-500 hover:bg-green-700 text-white p-2 rounded-md"
                      >
                        <svg
                          className="w-4 h-4"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke="currentColor"
                            strokeWidth="2"
                            d="M21 12c0 1.2-4.03 6-9 6s-9-4.8-9-6c0-1.2 4.03-6 9-6s9 4.8 9 6Z"
                          />
                          <path
                            stroke="currentColor"
                            strokeWidth="2"
                            d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                          />
                        </svg>
                      </Link>
                    </div>
                  </div>
                  <div className="flex justify-between items-center gap-4">
                    <span className="capitalize text-sm flex items-center gap-2 text-gray-500">
                      <svg
                        className="w-6 h-6"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3 4a1 1 0 0 0-.822 1.57L6.632 12l-4.454 6.43A1 1 0 0 0 3 20h13.153a1 1 0 0 0 .822-.43l4.847-7a1 1 0 0 0 0-1.14l-4.847-7a1 1 0 0 0-.822-.43H3Z"
                          clipRule="evenodd"
                        />
                      </svg>
                      état
                    </span>
                    <span
                      className={`px-2 text-sm border-2 rounded-md ${
                        item.is_active
                          ? "text-green-500 border-green-500"
                          : "text-red-500 border-red-500"
                      }`}
                    >
                      {item.is_active ? "Actif" : "Inactif"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-2">
                    <span className="flex items-center gap-3 text-sm">
                      <svg
                        className="w-6 h-6 text-gray-500"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                        />
                      </svg>
                      {item.stock_items?.length} Produits enregistrés
                    </span>
                    <Link
                      to={`/stock/sell-point-details/${id}/category/${item.id}`}
                      className="flex"
                    >
                      {/* Icônes de navigation */}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex gap-2 items-center justify-center flex-col h-32">
            <svg
              className="w-12 h-12 text-gray-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9Z"
              />
            </svg>
            <p className="text-gray-500 text-xl">Aucune catégorie trouvée</p>
          </div>
        )}
      </div>

      <div className="m-4 rounded-md p-1 space-y-4">
        <div className="flex-grow flex flex-col gap-4 bg-white rounded-md mt-4 p-4 h-max min-h-[35rem]">
          <div className="h-60 flex-1 max-w-full">
            <h2 className="text-xl font-medium mb-2">
              Stock : Produits en unité
            </h2>
            <ChartColumn
              categories={articlesStatistics.unite.labels} // Les libellés pour l'axe X
              data={articlesStatistics.unite.values} // Les valeurs pour les barres
              name="Produit en unité"
              titlex="Arcticles"
              titley="Quantité"
              style={{ width: "100%", height: "100%" }} // S'assurer que le graphique occupe tout l'espace
            />
          </div>
        </div>
      </div>

      {/* Modales */}
      {(modalType === "add" || modalType === "edit") && (
        <div className="fixed top-0 left-0 inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute top-0 left-0 inset-0 bg-black bg-opacity-30 backdrop-blur-sm cursor-pointer"
            onClick={closeModal}
          ></div>
          <div className="min-h-max bg-white z-50 mx-auto my-12 rounded-md p-4 shadow-lg max-w-lg">
            <div className="flex gap-4 relative">
              <svg
                className={`w-8 h-8 ${
                  modalType === "edit" ? "text-amber-500" : "text-green-500"
                }`}
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeWidth="2"
                  d="M3 15v3c0 .5523.44772 1 1 1h8v-8m-9 4v-4m0 4h9m-9-4V6c0-.55228.44772-1 1-1h16c.5523 0 1 .44772 1 1v4M3 11h11m6.25 5c0 1.2426-1.0073 2.25-2.25 2.25M20.25 16c0-1.2426-1.0073-2.25-2.25-2.25M20.25 16H21m-3 2.25c-1.2426 0-2.25-1.0074-2.25-2.25M18 18.25V19m-2.25-3c0-1.2426 1.0074-2.25 2.25-2.25M15.75 16H15m3-2.25V13m-1.591 1.409-.5303-.5303m4.2426 4.2426-.5303-.5303m-3.182 0-.5303.5303m4.2426-4.2426-.5303.5303"
                />
              </svg>
              <div className="text-sm">
                <h3 className="font-semibold">
                  {modalType === "add" ? "Ajouter" : "Modifier"} une catégorie
                </h3>
                <p className="text-xs">
                  {modalType === "add"
                    ? "Ajouter une nouvelle catégorie"
                    : "Modifier la catégorie"}
                </p>
              </div>

              <button
                onClick={closeModal}
                className="w-10 h-10 rounded-full bg-gray-200 grid justify-center items-center absolute -top-1 right-0 border border-gray-300 text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18 17.94 6M18 18 6.06 6"
                  />
                </svg>
              </button>
            </div>

            {validationErrors.error && (
              <p className="p-2 text-white bg-red-400 text-sm mt-4 rounded-md">
                {validationErrors.error}
              </p>
            )}

            {validationErrors.nom_magasin && (
              <p className="p-2 text-white bg-red-400 text-sm mt-4 rounded-md">
                {validationErrors.nom_magasin}
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-5 py-5">
              <div className="">
                <Dropzone
                  accept={{
                    "image/png": [".png"],
                    "image/jpeg": [".jpg", ".jpeg"],
                    "image/gif": [".gif"],
                    "image/svg+xml": [".svg"],
                  }}
                  message="Ajouter une photo"
                  textColor={modalType === "add" ? "green" : "amber"}
                  onFileUpload={handleFileUpload}
                  maxSize={2 * 1024 * 1024} // 2MB
                />
                {modalType === "edit" && currentCategory?.photo && !photo && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">Image actuelle :</p>
                    <img
                      src={MEDIA_URL + currentCategory.photo}
                      alt="Actuelle"
                      className="mt-1 h-24 w-auto rounded-md"
                    />
                  </div>
                )}
                ``
                {validationErrors.photo && (
                  <p className="text-red-500 text-sm text-center">
                    {validationErrors.photo}
                  </p>
                )}
              </div>

              <div className="">
                <div className="flex gap-2 sm:gap-4 sm:items-center sm:justify-between max-sm:flex-col">
                  <label htmlFor="nom" className="text-sm font-medium">
                    Nom de la catégorie
                  </label>
                  <input
                    type="text"
                    id="nom"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    placeholder="Vin rouge"
                    className="p-2 border rounded-md focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
                    required
                  />
                </div>
                {validationErrors.nom && (
                  <p className="text-red-500 text-sm">{validationErrors.nom}</p>
                )}
              </div>
              <div className="grid gap-4 grid-cols-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="p-2 rounded-md border-2 bg-gray-100 hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className={`p-2 rounded-md border-2 ${
                    modalType === "add"
                      ? "bg-green-500 hover:bg-green-600"
                      : "bg-amber-500 hover:bg-amber-600"
                  } text-white`}
                >
                  {modalType === "add" ? "Ajouter" : "Modifier"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalType === "deactivate" && (
        <div className="fixed top-0 left-0 inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute top-0 left-0 inset-0 bg-black bg-opacity-30 backdrop-blur-sm cursor-pointer"
            onClick={closeModal}
          ></div>
          <div className="relative p-4 w-full max-w-md max-h-full">
            <div className="relative bg-white rounded-lg shadow">
              <button
                type="button"
                onClick={closeModal}
                className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              >
                <svg
                  className="w-3 h-3"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 14 14"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M1 1l6 6m0 0l6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
                <span className="sr-only">Close modal</span>
              </button>
              <div className="p-4 md:p-5">
                <h3 className="mb-5 text-xl font-semibold flex items-center gap-4">
                  <svg
                    className="h-8 w-8 text-red-500"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeWidth="2"
                      d="m6 6 12 12m3-6a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                  Désactiver la catégorie
                </h3>
                <p className="mb-5 text-lg font-normal text-gray-500">
                  Êtes-vous sûr de vouloir désactiver cette catégorie ? Cette
                  action est réversible.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={handleDeactivate}
                    className="text-white bg-red-600 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm items-center px-5 py-2.5 text-center"
                  >
                    Désactiver
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="py-2.5 px-5 ms-3 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-amber-500 focus:z-10 focus:ring-4 focus:ring-gray-100"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
